import {snakeToCamelCase} from 'util/NameUtil';

import BaseFieldTranslator from './BaseFieldTranslator';
import {TranslatableValue} from './DeclarativeTranslator';
import {PossibleTypesMapping} from './PossibleTypesMapping';
import {GraphQLSelectionSet, RESTField} from './preprocessedData';

export default class SimpleNameFieldTranslator extends BaseFieldTranslator {
  /**
   * Translator that handles a few basic name changes (as well as fields being the same name):
   *
   * Examples:
   *    - x <-> y (custom name specified)
   *    - x <-> x
   *    - xJson <-> x
   *    - x <-> X (case mismatch)
   *    - abc_def <-> abcDef
   */

  public static tryParse(
    declarationName: string,
    parsedRESTField: RESTField,
    graphQLSelectionSet: GraphQLSelectionSet,
    possibleTypesMapping: PossibleTypesMapping
  ): BaseFieldTranslator | null {
    if (parsedRESTField.customGraphQLName) {
      return new SimpleNameFieldTranslator(
        parsedRESTField.fieldName,
        parsedRESTField.customGraphQLName,
        parsedRESTField.getExcludeCondition()
      );
    }

    const fieldsToTry = [
      parsedRESTField.fieldName,
      `${parsedRESTField.fieldName}Json`,
      snakeToCamelCase(parsedRESTField.fieldName),
    ];
    const foundGQLField = fieldsToTry.find((field) => graphQLSelectionSet.fieldsByName[field] != null);
    if (foundGQLField) {
      return new SimpleNameFieldTranslator(
        parsedRESTField.fieldName,
        foundGQLField,
        parsedRESTField.getExcludeCondition()
      );
    }

    const caseInsensitiveMatch = Object.keys(graphQLSelectionSet.fieldsByName).find(
      (field) => field.toLowerCase() === parsedRESTField.fieldName.toLowerCase()
    );
    if (caseInsensitiveMatch) {
      return new SimpleNameFieldTranslator(
        parsedRESTField.fieldName,
        caseInsensitiveMatch,
        parsedRESTField.getExcludeCondition()
      );
    }

    return null;
  }

  public getGQLValue(restObj: TranslatableValue): unknown {
    return restObj[this.restKey];
  }

  public getRESTValue(gqlObj: TranslatableValue): unknown {
    return gqlObj[this.graphQLKey];
  }
}
